import React, { useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import logo from "../../assets/images/logo-rewind.png";
import { ProviderChangeImpact } from "../../api/progression";
import studio from "./StudioModal.module.css";

interface ProviderChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    impact: ProviderChangeImpact | null;
    loading?: boolean;
}

function providerDisplayName(provider: string, t: any): string {
    if (provider === "HORAMA") return "Horama";
    if (provider === "WEAVIATE") return "Weaviate";
    return t("standardIA") || "OpenAI";
}

function providerChipColor(provider: string): string {
    if (provider === "HORAMA") return "#e8f5e9";
    if (provider === "WEAVIATE") return "#f3e5f5";
    return "#e3f2fd";
}

const ProviderChangeModal: React.FC<ProviderChangeModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    impact,
    loading = false,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    if (!isOpen || !impact) return null;

    const isHighSeverity = impact.severity === "high";
    const switchingToHorama = impact.newProvider === "HORAMA";
    const switchingToWeaviate = impact.newProvider === "WEAVIATE";
    const disconnectingWeaviate = impact.weaviateAction === "delete_collection";
    const connectingWeaviate = impact.weaviateAction === "create_collection";

    const modal = (
        <div className={studio.overlay}>
            <div className={`${studio.content} ${studio.deleteContent}`} ref={modalContentRef}>
                <div className={studio.logoWrap}>
                    <img src={logo} alt="Logo" />
                </div>

                <h1 className={studio.title}>{t("providerChangeWarning")}</h1>

                <div className={studio.message} style={{ textAlign: "left" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "12px",
                            marginBottom: "16px",
                            padding: "12px",
                        }}
                    >
                        <span
                            style={{
                                padding: "6px 12px",
                                backgroundColor: providerChipColor(impact.currentProvider),
                                borderRadius: "4px",
                                fontWeight: "500",
                            }}
                        >
                            {providerDisplayName(impact.currentProvider, t)}
                        </span>
                        <span style={{ fontSize: "20px" }}>→</span>
                        <span
                            style={{
                                padding: "6px 12px",
                                backgroundColor: providerChipColor(impact.newProvider),
                                borderRadius: "4px",
                                fontWeight: "500",
                            }}
                        >
                            {providerDisplayName(impact.newProvider, t)}
                        </span>
                    </div>

                    <p style={{ marginBottom: "16px" }}>{impact.message}</p>

                    <div
                        style={{
                            backgroundColor: isHighSeverity ? "#ffebee" : "#e8f5e9",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            marginBottom: "16px",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                            <span>{t("totalObjects")}:</span>
                            <strong>{impact.totalObjects}</strong>
                        </div>
                        {(connectingWeaviate || disconnectingWeaviate) && (
                            <>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                    <span>{t("objectsWithReferenceImage")}:</span>
                                    <strong>{impact.objectsWithImage ?? 0}</strong>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span>{t("objectsWithoutReferenceImage")}:</span>
                                    <strong>{impact.objectsWithoutImage ?? 0}</strong>
                                </div>
                            </>
                        )}
                        {isHighSeverity && switchingToHorama && (
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#c62828" }}>
                                <span>{t("objectsNeedingRemapping")}:</span>
                                <strong>{impact.affectedObjects.length}</strong>
                            </div>
                        )}
                        {!switchingToHorama && !connectingWeaviate && !disconnectingWeaviate && (
                            <div style={{ display: "flex", justifyContent: "space-between", color: "#2e7d32" }}>
                                <span>{t("objectsAutoMigrated")}:</span>
                                <strong>{impact.totalObjects}</strong>
                            </div>
                        )}
                    </div>

                    {isHighSeverity && impact.affectedObjects.length > 0 && (
                        <div style={{ marginBottom: "16px" }}>
                            <p style={{ fontWeight: "500", marginBottom: "8px", fontSize: "14px" }}>
                                {t("affectedObjectsList")}:
                            </p>
                            <div
                                style={{
                                    maxHeight: "120px",
                                    overflow: "auto",
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "8px",
                                    padding: "8px",
                                    fontSize: "13px",
                                }}
                            >
                                {impact.affectedObjects.slice(0, 8).map((obj, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            padding: "6px 8px",
                                            borderBottom:
                                                idx < Math.min(7, impact.affectedObjects.length - 1)
                                                    ? "1px solid #e0e0e0"
                                                    : "none",
                                        }}
                                    >
                                        <strong>{obj.name}</strong>
                                        <span style={{ color: "#999", marginLeft: "8px" }}>
                                            ({obj.currentKey || t("noKey")})
                                            {obj.issue === "missing_reference_image"
                                                ? ` — ${t("missingReferenceImage")}`
                                                : ""}
                                        </span>
                                    </div>
                                ))}
                                {impact.affectedObjects.length > 8 && (
                                    <p
                                        style={{
                                            margin: "8px 0 0 0",
                                            color: "#666",
                                            fontSize: "12px",
                                            textAlign: "center",
                                        }}
                                    >
                                        + {impact.affectedObjects.length - 8} {t("more")}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <div
                        style={{
                            padding: "12px",
                            borderRadius: "8px",
                            backgroundColor: isHighSeverity ? "#fff3e0" : "#e8f5e9",
                            borderLeft: `4px solid ${isHighSeverity ? "#ff9800" : "#4caf50"}`,
                            fontSize: "13px",
                        }}
                    >
                        {connectingWeaviate ? (
                            <>
                                <strong style={{ color: "#6a1b9a" }}>{t("weaviateConnectTitle")}</strong>
                                <p style={{ margin: "8px 0 0 0" }}>{t("weaviateConnectWarning")}</p>
                            </>
                        ) : disconnectingWeaviate ? (
                            <>
                                <strong style={{ color: "#e65100" }}>{t("weaviateDisconnectTitle")}</strong>
                                <p style={{ margin: "8px 0 0 0" }}>{t("weaviateDisconnectWarning")}</p>
                            </>
                        ) : switchingToHorama ? (
                            <>
                                <strong style={{ color: "#e65100" }}>⚠️ {t("attention")}</strong>
                                <p style={{ margin: "8px 0 0 0" }}>{t("horamaMigrationWarning")}</p>
                            </>
                        ) : switchingToWeaviate ? (
                            <>
                                <strong style={{ color: "#6a1b9a" }}>Weaviate</strong>
                                <p style={{ margin: "8px 0 0 0" }}>{t("weaviateConnectWarning")}</p>
                            </>
                        ) : (
                            <>
                                <strong style={{ color: "#2e7d32" }}>✓ {t("autoMigration")}</strong>
                                <p style={{ margin: "8px 0 0 0" }}>{t("openaiMigrationInfo")}</p>
                            </>
                        )}
                    </div>
                </div>

                <div className={studio.buttonsCentered}>
                    <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                    <Button
                        variant={isHighSeverity ? "danger" : "outline"}
                        shape="pill"
                        label={loading ? t("saving") : t("confirmAndContinue")}
                        onClick={onConfirm}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default ProviderChangeModal;
